import {
  Component, Inject, ViewChild, ElementRef,
  AfterViewChecked, ChangeDetectorRef, OnInit, OnDestroy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SecurityContext } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ChatMessage, TreatmentGuideChatbotService
} from './treatment-guide-chatbot.service';

export interface ChatbotDialogData {
  document: { document_id: number; title?: string; url?: string; tags?: any[] };
}

@Component({
    selector: 'app-dialog-treatment-guide-chatbot',
    templateUrl: './dialog-treatment-guide-chatbot.component.html',
    styleUrls: ['./dialog-treatment-guide-chatbot.component.scss'],
    providers: [TreatmentGuideChatbotService],
    standalone: false
})
export class DialogTreatmentGuideChatbotComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('chatContainer') chatContainer: ElementRef;

  messages: ChatMessage[] = [];
  userInput = '';
  isLoading = false;
  isLoadingDocument = false;
  presetQuestions: string[] = [];
  isLoadingPresets = false;
  chatbotNotSupported = false;
  documentTitle: string;
  documentUrl: string;
  noticeDismissed = false;
  hasStartedChat = false;
  showScrollButton = false;
  currentFollowUpQuestions: string[] = [];

  private shouldScrollToBottom = false;
  private destroy$ = new Subject<void>();
  private askedQuestions = new Set<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChatbotDialogData,
    private dialogRef: MatDialogRef<DialogTreatmentGuideChatbotComponent>,
    private chatbotService: TreatmentGuideChatbotService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.documentTitle = data.document?.title || 'Treatment Guide';
    this.documentUrl = data.document?.url || '';
  }

  ngOnInit(): void {
    this.initConversation();
  }

  ngOnDestroy(): void {
    this.chatbotService.resetConversation();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  onChatScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.showScrollButton = distanceFromBottom > 100;
  }

  scrollToBottomClicked(): void {
    this.scrollToBottom();
  }

  formatMessage(text: string): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, this.renderMarkdown(text)) ?? '';
  }

  sendMessage(question?: string): void {
    const text = (question ?? this.userInput).trim();
    if (!text || this.isLoading) return;

    this.hasStartedChat = true;
    this.askedQuestions.add(text);
    this.currentFollowUpQuestions = [];

    this.messages.push({ role: 'user', content: text, timestamp: new Date() });
    this.userInput = '';
    this.isLoading = true;
    this.shouldScrollToBottom = true;

    this.chatbotService.sendMessage(text).pipe(takeUntil(this.destroy$)).subscribe(
      (response) => {
        if (response.error) {
          this.messages.push({ role: 'assistant', content: `Error: ${response.error}`, timestamp: new Date() });
        } else {
          const parsed = this.parseResponse(response.message);
          this.messages.push({
            role: 'assistant',
            content: parsed.answer,
            source: parsed.source,
            timestamp: new Date()
          });
          this.updateFollowUpQuestions();
        }
        this.isLoading = false;
        this.shouldScrollToBottom = true;
        this.cdr.detectChanges();
      },
      () => {
        this.messages.push({
          role: 'assistant',
          content: 'Error: An unexpected error occurred.',
          timestamp: new Date()
        });
        this.isLoading = false;
        this.shouldScrollToBottom = true;
        this.cdr.detectChanges();
      }
    );
  }

  clearConversation(): void {
    this.messages = [];
    this.hasStartedChat = false;
    this.currentFollowUpQuestions = [];
    this.askedQuestions.clear();
    this.showScrollButton = false;
    this.chatbotService.resetConversation();
    this.initConversation();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  openPdf(): void {
    if (this.documentUrl) {
      window.open(this.documentUrl, '_blank');
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  dismissNotice(): void {
    this.noticeDismissed = true;
  }

  private initConversation(): void {
    this.isLoadingDocument = true;

    this.chatbotService.startConversation(this.data.document.document_id).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.isLoadingDocument = false;
        this.cdr.detectChanges();
        this.generateDynamicPresets();
      },
      () => {
        this.isLoadingDocument = false;
        this.cdr.detectChanges();
      }
    );
  }

  private generateDynamicPresets(): void {
    this.isLoadingPresets = true;
    this.chatbotService.generatePresetQuestions().pipe(takeUntil(this.destroy$)).subscribe(
      (questions) => {
        this.isLoadingPresets = false;
        if (questions.length === 0) {
          this.chatbotNotSupported = true;
        } else {
          this.presetQuestions = questions;
        }
        this.chatbotService.resetConversation();
        this.isLoadingDocument = true;
        this.chatbotService.startConversation(this.data.document.document_id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.isLoadingDocument = false;
            this.cdr.detectChanges();
          });
        this.cdr.detectChanges();
      },
      () => {
        this.isLoadingPresets = false;
        this.cdr.detectChanges();
      }
    );
  }

  private updateFollowUpQuestions(): void {
    const unused = this.presetQuestions.filter(q => !this.askedQuestions.has(q));
    this.currentFollowUpQuestions = unused.slice(0, 3);
  }

  private renderMarkdown(text: string): string {
    const lines = text.split('\n');
    let html = '';
    let inUl = false;
    let inOl = false;

    for (const rawLine of lines) {
      const line = this.escapeHtml(rawLine);
      const bulletMatch = line.match(/^[\s]*[-*]\s+(.+)/);
      const numberedMatch = line.match(/^[\s]*\d+\.\s+(.+)/);

      if (bulletMatch) {
        if (inOl) { html += '</ol>'; inOl = false; }
        if (!inUl) { html += '<ul>'; inUl = true; }
        html += `<li>${this.inlineMarkdown(bulletMatch[1])}</li>`;
      } else if (numberedMatch) {
        if (inUl) { html += '</ul>'; inUl = false; }
        if (!inOl) { html += '<ol>'; inOl = true; }
        html += `<li>${this.inlineMarkdown(numberedMatch[1])}</li>`;
      } else {
        if (inUl) { html += '</ul>'; inUl = false; }
        if (inOl) { html += '</ol>'; inOl = false; }
        if (line.trim()) {
          html += `<p>${this.inlineMarkdown(line)}</p>`;
        }
      }
    }

    if (inUl) html += '</ul>';
    if (inOl) html += '</ol>';

    return html || `<p>${this.escapeHtml(text)}</p>`;
  }

  private inlineMarkdown(text: string): string {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>');
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private parseResponse(text: string): { answer: string; source: string } {
    const sourceMatch = text.match(/SOURCE:\s*([\s\S]*?)$/im);
    if (sourceMatch) {
      const answer = text.substring(0, sourceMatch.index).trim();
      const source = sourceMatch[1].trim();
      return { answer, source };
    }
    return { answer: text, source: '' };
  }

  private scrollToBottom(): void {
    try {
      if (this.chatContainer) {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
        this.showScrollButton = false;
      }
    } catch (e) {}
  }
}
